import { useEffect, useState } from "react";
import axios from "axios";
import { client } from "../Clients";
import type { BCCADMINBaseModelsExchangeRatesResponse } from "../API/Models";

/** Live taux de change (exchange rates) for the FX ticker. */
export const useExchangeRates = () => {
	const [data, setData] = useState<BCCADMINBaseModelsExchangeRatesResponse | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		client.exchangeRates
			.apiExchangeRatesGet()
			.then((r) => {
				if (!cancelled) setData(r.data ?? null);
			})
			.catch(() => {
				if (!cancelled) setData(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	return { data, loading };
};

/** Per-language translation coverage of the French source content. */
export type LanguageCoverage = { code: string; label: string; flag: string; translated: number; total: number; percent: number };

// Target languages the BCC publishes into (the French source is the baseline, not shown here).
const COVERAGE_LANGUAGES: Array<{ code: string; label: string; flag: string }> = [
	{ code: "en", label: "English", flag: "gb" },
	{ code: "nl", label: "Nederlands", flag: "nl" },
	{ code: "de", label: "Deutsch", flag: "de" },
	{ code: "es", label: "Español", flag: "es" },
	{ code: "pt", label: "Português", flag: "pt" },
	{ code: "ar", label: "العربية", flag: "sa" },
	{ code: "sw", label: "Swahili", flag: "tz" },
	{ code: "ln", label: "Lingala", flag: "cd" }
];
/**
 * Current translation coverage: for each enabled target language, what share of the French
 * documents is fully translated (no missing or stale field). Fed by the per-field translation
 * ledger via GET /api/translations/overview/summary : corpus-wide aggregates only (a few hundred
 * bytes; the widget never needs the per-document rows), one call per language, in parallel.
 * Live snapshot, no stored history.
 */
export const useTranslationCoverage = () => {
	const [data, setData] = useState<LanguageCoverage[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// Only show languages that are actually enabled in the site's language config
				// (the "languages" singleton). Falls back to the full target list on any error.
				let activeLanguages = COVERAGE_LANGUAGES;
				try {
					const langRes = await client.content.apiContentSchemaKeyLanguageGet("languages", "default");
					const configured = (langRes.data?.current?.data as { languages?: Array<{ code?: string; isDisabled?: boolean }> } | undefined)?.languages;
					if (Array.isArray(configured)) {
						const enabled = new Set(
							configured.filter((l) => l?.code && !l.isDisabled).map((l) => l.code!.trim().toLowerCase())
						);
						activeLanguages = COVERAGE_LANGUAGES.filter((l) => enabled.has(l.code.toLowerCase()));
					}
				} catch {
					/* keep the full target list */
				}

				const perLanguage = await Promise.all(
					activeLanguages.map(async (lang) => {
						try {
							const res = await axios.get<{ translatedDocs?: number; totalDocs?: number }>(
								"/api/translations/overview/summary",
								{ params: { language: lang.code }, timeout: 25000 }
							);
							const translated = res.data?.translatedDocs ?? 0;
							const total = res.data?.totalDocs ?? 0;
							return { ...lang, translated, total, percent: total > 0 ? Math.round((translated / total) * 100) : 0 };
						} catch {
							return { ...lang, translated: 0, total: 0, percent: 0 };
						}
					})
				);
				if (!cancelled) setData(perLanguage.sort((a, b) => b.percent - a.percent));
			} catch {
				if (!cancelled) setData([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return { data, loading };
};
